/**
 * My Safe Spaces Website - Footer Component
 * ==========================================
 * Site footer with navigation links and contact info
 *
 * UPDATED: Added Careers link in footer navigation
 * UPDATED: Using centralized constants for contact info and social links
 */

import { Link } from 'react-router-dom';
import {
  Mail,
  Phone,
  MapPin,
  Linkedin,
  Twitter,
  Instagram,
  Facebook,
} from 'lucide-react';
import { CONTACT, SOCIAL_LINKS, COMPANY } from '../config/constants';
import { useContactModal } from '../context/ContactModalContext';
import './Footer.css';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { openContactModal } = useContactModal();
  return (
    <footer className="footer">
      <div className="container">
        {/* Main Footer Grid - 5 columns */}
        <div className="footer-grid">
          {/* Brand Section */}
          <div className="footer-brand">
            <Link to="/" className="footer-logo">
              <img src="/mss/My Safe Spaces Logo - White.png" alt="My Safe Spaces" />
            </Link>
            <p className="footer-tagline">
              {COMPANY.tagline}
            </p>
            <div className="footer-social">
              <a href={SOCIAL_LINKS.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <Linkedin size={16} />
              </a>
              <a href={SOCIAL_LINKS.twitter} target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                <Twitter size={16} />
              </a>
              <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <Instagram size={16} />
              </a>
              <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <Facebook size={16} />
              </a>
            </div>
          </div>

          {/* Solutions Links */}
          <div className="footer-links">
            <h4>Solutions</h4>
            <ul>
              <li><Link to="/schools">Schools (SWISS)</Link></li>
              <li><Link to="/higher-education">Higher Education (SWISS)</Link></li>
              <li><Link to="/workplace">Workplace (CREW)</Link></li>
              <li><Link to="/professional">Professional (ELEVATE)</Link></li>
              <li><Link to="/personal">Personal Wellness</Link></li>
            </ul>
          </div>

          {/* Platform Links */}
          <div className="footer-links">
            <h4>Platform</h4>
            <ul>
              <li><Link to="/intellicare-ai">IntellicareAI</Link></li>
              <li><Link to="/thrive-academy">Thrive Academy</Link></li>
            </ul>
          </div>

          {/* Resources Links */}
          <div className="footer-links">
            <h4>Resources</h4>
            <ul>
              <li><Link to="/resources/blogs">Blogs</Link></li>
              <li><Link to="/resources/awareness">Awareness</Link></li>
            </ul>
          </div>

          {/* Company Links */}
          <div className="footer-links">
            <h4>Company</h4>
            <ul>
              <li><Link to="/about">About Us</Link></li>
              <li><Link to="/about/leadership">Leadership</Link></li>
              <li><Link to="/about/strategic-advisors">Strategic Advisors</Link></li>
              <li><Link to="/about/certification-council">Certification Council</Link></li>
              <li><Link to="/about/partners">Partners</Link></li>
              <li><a href="https://careers.mysafespaces.org">Careers</a></li>
              <li><a href="#contact" onClick={(e) => { e.preventDefault(); openContactModal(); }}>Contact Us</a></li>
            </ul>
          </div>
        </div>

        {/* Contact Row - horizontal bar below grid */}
        <div className="footer-contact-row">
          <h4>Contact Us</h4>
          <div className="contact-items">
            <div className="contact-item">
              <Mail size={14} />
              <a href={`mailto:${CONTACT.email.general}`}>{CONTACT.email.general}</a>
            </div>
            <div className="contact-item">
              <Phone size={14} />
              <a href={CONTACT.phone.href}>{CONTACT.phone.display}</a>
            </div>
            <div className="contact-item">
              <MapPin size={14} />
              <span>{CONTACT.location.primary} | Operations: {CONTACT.location.operations}</span>
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="footer-bottom">
          <p>&copy; {currentYear} {COMPANY.legalName}. All rights reserved.</p>
          <div className="footer-legal">
            <Link to="/privacy-policy">Privacy Policy</Link>
            <Link to="/terms-of-service">Terms of Service</Link>
            <Link to="/accessibility">Accessibility</Link>
          </div>
          <span className="footer-love">Made with <span>❤️</span> for mental wellness in India</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
